import React from 'react';
import Text from '@zg-rentals/multibrand-ui/lib/Text';
import Button from '@zg-rentals/multibrand-ui/lib/Button';
import Row from '@zg-rentals/multibrand-ui/lib/Row';
import Col from '@zg-rentals/multibrand-ui/lib/Col';
import HR from '@zg-rentals/multibrand-ui/lib/HR';
import Modal from '@common/components/Modal';
import Section from '@rental-manager/components/Section';
import { renderDetailRows } from '@rental-manager/components/DetailRows';
import type { DetailRow } from '@rental-manager/components/DetailRows';
import DebouncedButton from '@common/components/DebouncedButton';
import SpinnerContainer from '@common/components/SpinnerContainer';
import userPersonas from '@common/constants/userPersonas';
import DetailsCard from '@rental-manager/components/DetailsCard';
import WhatsNextPage from '@rental-manager/pages/Dashboard/OneTimePaymentModalV2/WhatsNextPage';
import type { MoveInCostsSettingsInterface } from '@rental-manager/pages/Dashboard/types';

interface ConfirmMoveInCostsPageProps {
  goToDashboard: (tenantFirstName: string) => void;
  onSubmit: () => any;
  onClose: () => void;
  onBack: () => void;
  listingName: string;
  moveInCosts: Array<DetailRow>;
  goToMoveInCostsModal: () => void;
  moveInCostsSettings: MoveInCostsSettingsInterface;
  tenantFirstName: string;
}

interface ConfirmMoveInCostsPageState {
  showLoadingScreen: boolean;
  showSuccessScreen: boolean;
}

class ConfirmMoveInCostsPage extends React.Component<ConfirmMoveInCostsPageProps, ConfirmMoveInCostsPageState> {
  state = {
    showLoadingScreen: false,
    showSuccessScreen: false,
  };

  showLoadingScreen = () => {
    this.setState({ showLoadingScreen: true });
  };

  hideLoadingScreen = () => {
    this.setState({ showLoadingScreen: false });
  };

  onSubmit = async () => {
    this.showLoadingScreen();
    const response = await this.props.onSubmit();
    this.hideLoadingScreen();

    if (response.error) {
      return;
    }

    this.setState({ showSuccessScreen: true });
  };

  onClose = () => {
    if (this.state.showSuccessScreen) {
      this.props.goToDashboard(this.props.tenantFirstName);
    } else {
      this.props.onClose();
    }
  };

  onBack = () => {
    this.props.onBack();
  };

  startOver = () => {
    this.props.goToMoveInCostsModal();
  };

  goToDashboard = () => {
    this.props.goToDashboard(this.props.tenantFirstName);
  };

  renderNoMoveInCostsSelectedModal() {
    return (
      <>
        <Section>
          <Text type="h3">Would you like to start over?</Text>
          <Text>
            If you don’t want to collect a security deposit, prorated rent, or any other moving costs, you may return to
            the dashboard, or start over.
          </Text>
        </Section>
        <Row>
          <Col>
            <Button type="tertiary" full onClick={this.onClose}>
              Return to dashboard
            </Button>
          </Col>
        </Row>
        <Row>
          <Col>
            <Button type="primary" full onClick={this.startOver}>
              Start over
            </Button>
          </Col>
        </Row>
      </>
    );
  }

  renderMICConfirmationDetailsModal() {
    const { listingName, moveInCosts } = this.props;
    return (
      <>
        {this.state.showLoadingScreen && <SpinnerContainer />}

        <Section>
          <Text type="h3">Confirm info and invite tenant</Text>
          <Text>Please review the information you provided for {listingName} and confirm it&apos;s correct.</Text>
        </Section>
        <Section>
          <HR />
          <DetailsCard className="fs-exclude" title="Move-in costs" showEdit={false}>
            {renderDetailRows(moveInCosts)}
          </DetailsCard>
        </Section>
        <Section>
          <DebouncedButton type="primary" full onClick={this.onSubmit}>
            Confirm and invite tenant
          </DebouncedButton>
        </Section>
      </>
    );
  }

  render() {
    const { moveInCostsSettings } = this.props;
    const { securityDepositSettings, proratedRentSettings, otherSettings } = moveInCostsSettings;
    const renderMICConfirmationModal =
      securityDepositSettings.securityDepositStartsOpen === false &&
      proratedRentSettings.proratedRentStartsOpen === false &&
      otherSettings.length === 0
        ? this.renderNoMoveInCostsSelectedModal()
        : this.renderMICConfirmationDetailsModal();
    return (
      <Modal
        persona={userPersonas.LANDLORD}
        header="MOVE-IN COSTS"
        onRequestClose={this.onClose}
        onClickBack={this.state.showSuccessScreen ? null : this.onBack}
        dataAutoTestId="landlord-dashboard-confirmation"
      >
        {this.state.showSuccessScreen ? (
          <WhatsNextPage goToDashboard={this.goToDashboard} />
        ) : (
          renderMICConfirmationModal
        )}
      </Modal>
    );
  }
}
export default ConfirmMoveInCostsPage;
